import { useIonToast } from '@ionic/react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMeasureRect } from 'react-use/lib/useMeasure';
import { makeErrorToast } from '../../util';
import { logger } from 'services/Logger';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from '../../util/constants';

const isWeb =
  typeof window !== 'undefined' &&
  (!window.cordova || (window as any).Device?.platform === 'web');

let CameraFrame: any = null;
if (!isWeb) {
  CameraFrame = require('camera-frame').CameraFrame;
} else {
  // Im Web: Dummy-Objekt, damit keine Fehler auftreten
  CameraFrame = {
    setSettings: async () => {},
    showPreview: async () => {},
    hidePreview: async () => {},
    destroy: async () => {},
    flipCamera: async () => {},
    setCamera: async () => {},
    availableCameras: async () => ({ cameras: [] }),
    moveCameraView: async () => {},
    zoom: async () => {},
    startRecording: async () => {},
    stopRecording: async () => ({ videoURL: '' }),
    captureImage: async () => ({ img: '' }),
    addListener: async () => ({
      remove: async () => {},
    }),
    removeAllListeners: async () => {},
    checkPermissions: async () => ({ camera: 'denied' }),
    requestPermissions: async () => ({ camera: 'denied' }),
  };
}

const useCameraFrame = ({ x, y, width, height }: UseMeasureRect) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1.0);
  const [toast, dismiss] = useIonToast();
  const [videoIsRecording, setIsRecording] = useState(false);
  const [cameraShown, setCameraShown] = useState(false);
  const [present] = useIonToast();

  const activateCamera = useCallback(async () => {
    try {
      var permissions = await CameraFrame.checkPermissions();
      if (permissions.camera.includes('prompt')) {
        permissions = await CameraFrame.requestPermissions({ withAudio: true });
      }
      if (permissions.camera === 'denied') {
        return;
      }
      try {
        await CameraFrame.showPreview({ toBack: true });
      } catch (error) {
        logger.error(error instanceof Error ? error.message : String(error));
      }
      setCameraShown(true);
    } catch (error) {
      logger.error(error instanceof Error ? error.message : String(error));
      present({
        message: t('CAMERA_PERMISSION_DENIED'),
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    }
  }, [setCameraShown, dismiss, toast, t]);

  const deactivateCamera = useCallback(async () => {
    try {
      CameraFrame.hidePreview();
      setCameraShown(false);
    } catch (error) {
      logger.error(error instanceof Error ? error.message : String(error));
      toast(makeErrorToast(t('ERROR'), error as string, dismiss));
    }
  }, [setCameraShown, dismiss, toast, t]);

  useEffect(() => {
    if (!cameraShown || width === 0 || height === 0) return;
    /* CameraFrame.moveCameraView({
     *   x,
     *   y,
     *   width,
     *   height,
     * }); */
  }, [x, y, width, height, cameraShown]);

  // useEffect(() => {
  //   CameraFrame.setSettings({
  //     captureSettings: {
  //       captureAudio: true,
  //       enableZoom: true,
  //       quality: 'hd1920x1080',
  //     },
  //     viewSettings: {
  //       resizeToFit: true,
  //       /* resizeToFit: false */
  //     },
  //   });
  //   const zoomHandler = CameraFrame.addListener(
  //     'ZoomChanged',
  //     (data: { from: number; to: number }) => {
  //       setZoom(data.to);
  //     }
  //   );
  //   return () => {
  //     zoomHandler?.remove();
  //   };
  // }, []);

  const startVideoRecording = async (fileName: string) => {
    if (!cameraShown) return;
    setIsRecording(true);
    return CameraFrame.startRecording({ fileName });
  };

  const stopVideoRecording = async () => {
    if (!cameraShown) return;

    const ret = await CameraFrame.stopRecording();
    try {
      const { videoURL } = ret;
      return videoURL;
    } catch (e: any) {
      return null;
    } finally {
      setIsRecording(false);
    }
  };

  return {
    zoom,
    activateCamera,
    deactivateCamera,
    startVideoRecording,
    stopVideoRecording,
    videoIsRecording,
    cameraShown,
    setCameraShown,
  };
};

export default useCameraFrame;
